import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PdcStateItem } from 'data/model/pdc'
import { produce } from 'immer'

import { CollectionFormPayload } from '../api/collections'
import { CollectionMap, CollectionsState, PlaceMap, SortOrder, Status } from '../model'

const initialState: CollectionsState = {
  collectionMap: {},
  error: undefined,
  order: SortOrder.dateAdded,
  pageToken: undefined,
  placeMap: {},
  status: Status.unknown,
}

const slice = createSlice({
  initialState,
  name: 'collections',
  reducers: {
    createCollection: {
      prepare: (collection: CollectionFormPayload) => {
        return { payload: { collection } }
      },
      reducer: (state: CollectionsState) => {
        return produce(state, (draft) => {
          draft.status = Status.createCollectionPending
        })
      },
    },
    deleteCollection: {
      prepare: (collectionId: string) => {
        return { payload: { collectionId } }
      },
      reducer: (state: CollectionsState) => {
        return produce(state, (draft) => {
          draft.status = Status.deleteCollectionPending
        })
      },
    },
    deletePlaceFromCollection: {
      prepare: (placeId: string, collectionId: string) => {
        return { payload: { collectionId, placeId } }
      },
      reducer: (state: CollectionsState) => {
        return produce(state, (draft) => {
          draft.status = Status.deletePlaceInCollectionPending
        })
      },
    },
    editCollection: {
      prepare: (collection: CollectionFormPayload) => {
        return { payload: { collection } }
      },
      reducer: (state: CollectionsState) => {
        return produce(state, (draft) => {
          draft.status = Status.editCollectionPending
        })
      },
    },
    fetchCollectionsAndPlaces: {
      prepare: (pageToken?: string) => {
        return { payload: { pageToken } }
      },
      reducer: (state: CollectionsState) => {
        return produce(state, (draft) => {
          draft.status = Status.fetchCollectionsAndPlacesPending
        })
      },
    },
    savePlaceInCollection: {
      prepare: (pdcItem: PdcStateItem, collectionId: string) => {
        return { payload: { collectionId, pdcItem } }
      },
      reducer: (state: CollectionsState) => {
        return produce(state, (draft) => {
          draft.status = Status.savePlaceInCollectionPending
        })
      },
    },
    setCollections(state: CollectionsState, action: PayloadAction<CollectionMap>) {
      return produce(state, (draft) => {
        draft.collectionMap = action.payload
      })
    },
    setError(state: CollectionsState, action: PayloadAction<string>) {
      return produce(state, (draft) => {
        draft.error = action.payload
      })
    },
    setLastVisitedCollectionId(state: CollectionsState, action: PayloadAction<string | undefined>) {
      return produce(state, (draft) => {
        draft.lastVisitedCollectionId = action.payload
      })
    },
    setLoadingStatus(state: CollectionsState, action: PayloadAction<Status>) {
      return produce(state, (draft) => {
        draft.status = action.payload
      })
    },
    setOrder(state: CollectionsState, action: PayloadAction<SortOrder>) {
      return produce(state, (draft) => {
        draft.order = action.payload
      })
    },
    setPaginationToken(state: CollectionsState, action: PayloadAction<string | undefined>) {
      return produce(state, (draft) => {
        draft.pageToken = action.payload
      })
    },
    setPlaces(state: CollectionsState, action: PayloadAction<PlaceMap>) {
      return produce(state, (draft) => {
        draft.placeMap = action.payload
      })
    },
  },
})

export const {
  createCollection,
  deleteCollection,
  deletePlaceFromCollection,
  editCollection,
  fetchCollectionsAndPlaces,
  savePlaceInCollection,
  setCollections,
  setPlaces,
  setError,
  setOrder,
  setPaginationToken,
  setLoadingStatus,
  setLastVisitedCollectionId,
} = slice.actions
export const reducer = slice.reducer
